import { ChevronsUpDownIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Popover, PopoverPopup, PopoverTitle, PopoverTrigger } from "~/components/ui/popover";
import { DashboardSelect } from "./dashboardKit";
import {
  careNavigationTeamId,
  type CareNavigationTeamId,
  type CareNavigationStaffStatus,
} from "./careNavigationTeams";

export function CareNavigationStaffStatusFilter(props: {
  value: CareNavigationStaffStatus;
  onChange: (status: CareNavigationStaffStatus) => void;
}) {
  return (
    <label className="grid gap-1 text-xs">
      Staff status
      <DashboardSelect
        value={props.value}
        onChange={(event) => props.onChange(event.target.value as CareNavigationStaffStatus)}
      >
        <option value="active">Active staff</option>
        <option value="inactive">Inactive staff</option>
        <option value="all">All staff</option>
      </DashboardSelect>
    </label>
  );
}

export function CareNavigationTeamFilter(props: {
  team: CareNavigationTeamId | undefined;
  teams: Array<{ id: string; label: string }>;
  selected: string[];
  onChange: (team: CareNavigationTeamId | undefined) => void;
}) {
  const selectedTeam = props.team ?? (props.selected.length ? "custom" : "all");
  function selectTeam(value: string) {
    if (value === "all") {
      props.onChange(undefined);
      return;
    }
    const team = careNavigationTeamId(value);
    if (team) props.onChange(team);
  }
  return (
    <label className="grid gap-1 text-xs">
      Team
      <DashboardSelect value={selectedTeam} onChange={(event) => selectTeam(event.target.value)}>
        <option value="all">All teams</option>
        {props.team && !props.teams.some((team) => team.id === props.team) ? (
          <option value={props.team}>Manager not found in directory</option>
        ) : null}
        {selectedTeam === "custom" ? (
          <option value="custom" disabled>
            Custom CN selection
          </option>
        ) : null}
        {props.teams.map((team) => (
          <option key={team.id} value={team.id}>
            {team.label}
          </option>
        ))}
      </DashboardSelect>
    </label>
  );
}

export function CareNavigationNavigatorFilter(props: {
  options: string[];
  selected: string[];
  onChange: (names: string[]) => void;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(props.selected);
  const options = [...new Set([...props.options, ...props.selected])]
    .filter((name) => name.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((left, right) => left.localeCompare(right));
  const label =
    props.selected.length === 0
      ? "All care navigators"
      : props.selected.length === 1
        ? props.selected[0]
        : `${props.selected.length} care navigators`;

  function toggle(name: string) {
    setDraft(
      draft.includes(name) ? draft.filter((selected) => selected !== name) : [...draft, name],
    );
  }

  function changeOpen(nextOpen: boolean) {
    if (nextOpen) {
      setDraft(props.selected);
      setSearch("");
    }
    setOpen(nextOpen);
  }

  function apply() {
    props.onChange(draft);
    setOpen(false);
  }

  return (
    <div className="grid gap-1 text-xs">
      <span>Care navigators</span>
      <Popover open={open} onOpenChange={changeOpen}>
        <PopoverTrigger
          render={<Button variant="outline" size="sm" aria-label="Care navigators" />}
        >
          <span className="max-w-56 truncate">{label}</span>
          <ChevronsUpDownIcon className="size-3.5" aria-hidden />
        </PopoverTrigger>
        <PopoverPopup align="start" className="w-80">
          <div className="grid gap-3">
            <PopoverTitle>Care navigators</PopoverTitle>
            <p className="text-xs text-muted-foreground">
              Choose one or more care navigators, then Apply.
            </p>
            <Input
              aria-label="Search care navigators"
              placeholder="Search care navigators"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Button size="sm" variant="ghost" onClick={() => setDraft([])}>
              All care navigators
            </Button>
            <fieldset className="grid max-h-64 gap-2 overflow-y-auto">
              <legend className="sr-only">Select care navigators</legend>
              {options.map((name) => (
                <label key={name} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.includes(name)}
                    onChange={() => toggle(name)}
                  />
                  {name}
                </label>
              ))}
              {options.length === 0 ? <p>No care navigators found.</p> : null}
            </fieldset>
            <Button size="sm" onClick={apply}>
              Apply
            </Button>
          </div>
        </PopoverPopup>
      </Popover>
    </div>
  );
}
